import styled, { keyframes, css } from 'styled-components';
import t from './theme';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(0,224,122,0.5); }
  70% { box-shadow: 0 0 0 6px rgba(0,224,122,0); }
  100% { box-shadow: 0 0 0 0 rgba(0,224,122,0); }
`;

/* ---------------- Shell / layout ----------------
   Breaks out of MainArea's 32px padding and claims the full viewport height
   below the 80px app header, so the panes can establish their own scroll
   regions and the full-bleed 2-pane design renders properly. */
export const Shell = styled.div`
  display: flex;
  height: calc(100vh - 80px);
  min-height: 0;
  width: 100%;
  margin: -32px;
  width: calc(100% + 64px);
  background: ${t.color.canvas};
  font-family: ${t.font.base};
  color: ${t.color.text};
  overflow: hidden;
  border-radius: 0;

  @media (max-width: 768px) {
    margin: 0;
    width: 100%;
    height: calc(100vh - 80px);
  }
`;

export const Rail = styled.div`
  width: 34rem;
  min-width: 30rem;
  display: flex;
  flex-direction: column;
  background: ${t.color.surface};
  border-right: 1px solid ${t.color.line};
  ${({ hideOnMobile }) =>
    hideOnMobile &&
    css`
      @media (max-width: 768px) {
        display: none;
      }
    `}
`;

export const Stage = styled.div`
  flex: 1;
  display: flex;
  min-width: 0;
`;

export const ThreadCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0; /* allow the Canvas to shrink + scroll instead of overflowing */
  position: relative;
`;

/* ---------------- Rail header + search ---------------- */
export const RailHeader = styled.div`
  padding: 2rem 2rem 1.2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
export const RailTitle = styled.div`
  font-family: ${t.font.bold};
  font-size: 2.2rem;
  color: ${t.color.secondary};
`;
export const RailBadge = styled.span`
  font-family: ${t.font.medium};
  font-size: 1.2rem;
  color: ${t.color.primaryDark};
  background: ${t.color.primarySoft};
  border-radius: ${t.radius.pill};
  padding: 0.3rem 1rem;
`;

export const SearchPill = styled.div`
  margin: 0 1.6rem 1.2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  background: ${t.color.canvas};
  border: 1px solid transparent;
  border-radius: ${t.radius.pill};
  padding: 1rem 1.4rem;
  transition: border-color 0.15s, background 0.15s;
  &:focus-within {
    background: ${t.color.surface};
    border-color: ${t.color.primary};
  }
`;
export const SearchInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  font-size: 1.5rem;
  font-family: ${t.font.base};
  color: ${t.color.text};
  &::placeholder {
    color: ${t.color.faint};
  }
`;

export const RailList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.4rem 0.8rem 2rem;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${t.color.line};
    border-radius: 8px;
  }
`;

/* ---------------- Conversation card ---------------- */
export const Card = styled.button`
  width: 100%;
  text-align: start;
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1.1rem 1.2rem;
  margin: 0.3rem 0;
  border: none;
  cursor: pointer;
  border-radius: ${t.radius.md};
  background: ${({ active }) => (active ? t.color.primarySoft : 'transparent')};
  position: relative;
  transition: background 0.14s;
  animation: ${fadeIn} 0.2s ease both;
  &:hover {
    background: ${({ active }) => (active ? t.color.primarySoft : t.color.canvas)};
  }
  ${({ active }) =>
    active &&
    css`
      &::before {
        content: '';
        position: absolute;
        inset-inline-start: 0;
        top: 18%;
        height: 64%;
        width: 4px;
        border-radius: 4px;
        background: ${t.color.primary};
      }
    `}
`;

export const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;
export const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.8rem;
`;
export const CardName = styled.div`
  font-family: ${t.font.medium};
  font-size: 1.55rem;
  color: ${t.color.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
export const CardTime = styled.div`
  font-size: 1.15rem;
  color: ${t.color.faint};
  flex-shrink: 0;
`;
export const CardPreview = styled.div`
  font-size: 1.35rem;
  color: ${({ unread }) => (unread ? t.color.text : t.color.subtext)};
  font-family: ${({ unread }) => (unread ? t.font.medium : t.font.base)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 0.3rem;
  direction: ${({ dir }) => dir || 'ltr'};
`;
export const UnreadPill = styled.span`
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.6rem;
  border-radius: ${t.radius.pill};
  background: ${t.color.primary};
  color: ${t.color.secondary};
  font-family: ${t.font.bold};
  font-size: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

/* ---------------- Avatar ---------------- */
export const Avatar = styled.div`
  width: ${({ size }) => size || '4.4rem'};
  height: ${({ size }) => size || '4.4rem'};
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ src, bg }) =>
    src ? `url(${src}) center/cover no-repeat` : bg || t.color.canvas};
  color: ${({ fg }) => fg || t.color.subtext};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${t.font.bold};
  font-size: ${({ size }) => (size ? '1.6rem' : '1.5rem')};
  position: relative;
`;
export const PresenceDot = styled.span`
  position: absolute;
  bottom: 1px;
  inset-inline-end: 1px;
  width: 1.1rem;
  height: 1.1rem;
  border-radius: 50%;
  border: 2px solid ${t.color.surface};
  background: ${({ online }) => (online ? t.color.online : t.color.faint)};
  ${({ online }) =>
    online &&
    css`
      animation: ${pulse} 2s infinite;
    `}
`;

/* ---------------- Thread header ---------------- */
export const ThreadHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1.4rem 2rem;
  background: ${t.color.surface};
  border-bottom: 1px solid ${t.color.line};
`;
export const HeaderName = styled.div`
  font-family: ${t.font.bold};
  font-size: 1.7rem;
  color: ${t.color.secondary};
`;
export const HeaderSub = styled.div`
  font-size: 1.25rem;
  color: ${({ online }) => (online ? t.color.online : t.color.faint)};
  margin-top: 0.2rem;
`;
export const HeaderActions = styled.div`
  margin-inline-start: auto;
  display: flex;
  gap: 0.6rem;
`;
export const IconButton = styled.button`
  border: none;
  background: ${({ active }) => (active ? t.color.primarySoft : 'transparent')};
  color: ${({ active }) => (active ? t.color.primaryDark : t.color.subtext)};
  width: 4rem;
  height: 4rem;
  border-radius: ${t.radius.md};
  cursor: pointer;
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.14s, color 0.14s;
  &:hover {
    background: ${t.color.canvas};
  }
`;

/* ---------------- Message canvas ---------------- */
export const Canvas = styled.div`
  flex: 1;
  min-height: 0; /* required so this scroll region shrinks within the flex column */
  overflow-y: auto;
  padding: 2.4rem clamp(1.6rem, 8vw, 12rem);
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  background: ${t.color.surface};
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: transparent; border-radius: 8px; }
  &:hover::-webkit-scrollbar-thumb { background: ${t.color.line}; }
`;

export const DayDivider = styled.div`
  align-self: center;
  margin: 1.4rem 0 0.8rem;
  font-size: 1.2rem;
  color: ${t.color.subtext};
  background: ${t.color.surface};
  border: 1px solid ${t.color.line};
  padding: 0.4rem 1.2rem;
  border-radius: ${t.radius.pill};
`;

export const Row = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.8rem;
  margin-top: ${({ grouped }) => (grouped ? '0.15rem' : '1.1rem')};
  flex-direction: ${({ owned }) => (owned ? 'row-reverse' : 'row')};
  animation: ${fadeUp} 0.18s ease both;
`;
export const RowAvatarSlot = styled.div`
  width: 3.2rem;
  flex-shrink: 0;
`;
export const Bubble = styled.div`
  width: fit-content;
  max-width: 100%;
  padding: 1rem 1.3rem;
  border-radius: 1.8rem;
  font-size: 1.5rem;
  line-height: 1.45;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  direction: ${({ dir }) => dir || 'ltr'};
  position: relative;
  transition: background 0.15s;
  ${({ owned, grouped }) =>
    owned
      ? css`
          background: ${t.color.primarySoft};
          color: ${t.color.secondary};
          border: 1px solid rgba(0, 199, 114, 0.25);
          border-bottom-right-radius: ${grouped ? '1.8rem' : '0.6rem'};
        `
      : css`
          background: ${t.color.canvas};
          color: ${t.color.text};
          border: 1px solid ${t.color.line};
          border-bottom-left-radius: ${grouped ? '1.8rem' : '0.6rem'};
        `}
  a { color: ${t.color.primaryDark}; text-decoration: underline; }
`;
export const SenderLabel = styled.div`
  font-size: 1.2rem;
  font-family: ${t.font.medium};
  color: ${t.color.primaryDark};
  margin-bottom: 0.3rem;
`;
export const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  justify-content: ${({ owned }) => (owned ? 'flex-end' : 'flex-start')};
  font-size: 1.1rem;
  color: ${t.color.faint};
  margin: 0.2rem 0.6rem 0;
`;
export const ReadTick = styled.span`
  color: ${({ read }) => (read ? t.color.primaryDark : t.color.faint)};
  font-size: 1.2rem;
`;
export const SystemNote = styled.div`
  align-self: center;
  margin: 0.8rem 0;
  font-size: 1.25rem;
  color: ${t.color.subtext};
  background: ${t.color.surface};
  border-radius: ${t.radius.pill};
  padding: 0.4rem 1.4rem;
`;

/* ---------------- Media ---------------- */
export const MediaImage = styled.img`
  max-width: 28rem;
  max-height: 28rem;
  border-radius: 1.4rem;
  cursor: zoom-in;
  display: block;
`;
export const MediaVideo = styled.video`
  max-width: 32rem;
  border-radius: 1.4rem;
  display: block;
`;
export const FileCard = styled.a`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1.1rem 1.3rem;
  border-radius: ${t.radius.md};
  background: ${({ owned }) => (owned ? 'rgba(0,18,18,0.10)' : t.color.canvas)};
  text-decoration: none !important;
  color: inherit;
  min-width: 22rem;
`;
export const FileIcon = styled.div`
  width: 3.8rem;
  height: 3.8rem;
  border-radius: ${t.radius.sm};
  background: ${t.color.primarySoft};
  color: ${t.color.primaryDark};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  flex-shrink: 0;
`;
export const FileMeta = styled.div`
  min-width: 0;
  .name {
    font-family: ${t.font.medium};
    font-size: 1.4rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sub {
    font-size: 1.2rem;
    color: ${t.color.subtext};
  }
`;

/* PDF: card header + inline first-page preview inside the bubble. */
export const PdfBlock = styled.div`
  width: 30rem;
  max-width: 100%;
  border-radius: ${t.radius.md};
  background: ${({ owned }) => (owned ? 'rgba(0,18,18,0.10)' : t.color.canvas)};
  overflow: hidden;
`;
export const PdfHeader = styled.a`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1.1rem 1.3rem;
  text-decoration: none !important;
  color: inherit;
`;
export const PdfPreview = styled.div`
  position: relative;
  height: 34rem;
  border-top: 1px solid rgba(0, 18, 18, 0.08);
  background: #fff;
  cursor: pointer;
  iframe,
  embed {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
    /* Chrome/Edge honour these to hide the built-in viewer chrome. */
    pointer-events: none;
  }
  /* Click target over the (pointer-events:none) embed so the whole preview opens it. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }
`;
export const PdfFallback = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: ${t.color.subtext};
  background: ${t.color.canvas};
`;

export const CustomButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  border: none;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  background: ${t.color.primarySoft};
  color: ${t.color.secondary};
  padding: 1rem 1.4rem;
  border-radius: ${t.radius.md};
  font-family: ${t.font.medium};
  font-size: 1.45rem;
  img { width: 2.2rem; height: 2.2rem; }
`;

/* ---------------- Lightbox ---------------- */
export const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 12, 12, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: ${fadeIn} 0.15s ease;
  cursor: zoom-out;
  img {
    max-width: 92vw;
    max-height: 90vh;
    border-radius: 1rem;
    box-shadow: ${t.shadow.raised};
  }
`;

/* ---------------- Composer ---------------- */
export const Composer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  padding: 1.2rem clamp(1.2rem, 5vw, 6rem) 1.8rem;
  background: ${t.color.surface};
  border-top: 1px solid ${t.color.line};
`;
export const ComposerInputWrap = styled.div`
  flex: 1;
  background: ${t.color.canvas};
  border: 1px solid transparent;
  border-radius: ${t.radius.lg};
  padding: 0.6rem 1.4rem;
  display: flex;
  align-items: center;
  transition: border-color 0.15s, background 0.15s;
  &:focus-within {
    background: ${t.color.surface};
    border-color: ${t.color.primary};
  }
`;
export const Textarea = styled.textarea`
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  font-family: ${t.font.base};
  font-size: 1.5rem;
  line-height: 1.5;
  color: ${t.color.text};
  max-height: 14rem;
  padding: 0.6rem 0;
  &::placeholder {
    color: ${t.color.faint};
  }
`;
export const RoundButton = styled.button`
  width: 4.6rem;
  height: 4.6rem;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.9rem;
  transition: transform 0.1s, opacity 0.15s, background 0.15s;
  background: ${({ ghost }) => (ghost ? t.color.canvas : t.color.primary)};
  color: ${({ ghost }) => (ghost ? t.color.subtext : t.color.secondary)};
  &:hover { transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: default; transform: none; }
`;
export const HiddenFile = styled.input`
  display: none;
`;

/* ---------------- Detail panel ---------------- */
export const Detail = styled.div`
  width: 32rem;
  flex-shrink: 0;
  background: ${t.color.surface};
  border-inline-start: 1px solid ${t.color.line};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 2rem;
  overflow-y: auto;
  animation: ${fadeIn} 0.2s ease;
  @media (max-width: 1100px) {
    position: absolute;
    inset-inline-end: 0;
    top: 0;
    bottom: 0;
    box-shadow: ${t.shadow.raised};
    z-index: 5;
  }
`;
export const DetailName = styled.div`
  font-family: ${t.font.bold};
  font-size: 2rem;
  color: ${t.color.secondary};
  margin-top: 1.4rem;
`;
export const DetailRole = styled.div`
  font-size: 1.3rem;
  color: ${t.color.subtext};
  margin-top: 0.3rem;
  text-transform: capitalize;
`;
export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.2rem;
  width: 100%;
  margin-top: 2.6rem;
`;
export const StatCard = styled.div`
  background: ${t.color.canvas};
  border-radius: ${t.radius.md};
  padding: 1.6rem 1.2rem;
  text-align: center;
  &.full { grid-column: 1 / -1; }
  .value {
    font-family: ${t.font.bold};
    font-size: 2.4rem;
    color: ${t.color.primaryDark};
  }
  .label {
    font-size: 1.2rem;
    color: ${t.color.subtext};
    margin-top: 0.4rem;
  }
`;

/* ---------------- Group members panel ---------------- */
export const MembersPanel = styled.div`
  width: 30rem;
  flex-shrink: 0;
  background: ${t.color.surface};
  border-inline-start: 1px solid ${t.color.line};
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  @media (max-width: 1100px) {
    display: none;
  }
`;
export const MembersHeader = styled.div`
  padding: 2rem 2rem 1.2rem;
  border-bottom: 1px solid ${t.color.line};
`;
export const MembersGroupName = styled.div`
  font-family: ${t.font.bold};
  font-size: 1.7rem;
  color: ${t.color.secondary};
`;
export const MembersCount = styled.div`
  font-size: 1.3rem;
  color: ${t.color.subtext};
  margin-top: 0.3rem;
`;
export const MembersStatRow = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.4rem 2rem;
  border-bottom: 1px solid ${t.color.line};
`;
export const MembersStat = styled.div`
  flex: 1;
  text-align: center;
  .value {
    font-family: ${t.font.bold};
    font-size: 1.9rem;
    color: ${t.color.primaryDark};
  }
  .label {
    font-size: 1.1rem;
    color: ${t.color.subtext};
    margin-top: 0.2rem;
  }
`;
export const MembersListTitle = styled.div`
  font-size: 1.2rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${t.color.faint};
  padding: 1.6rem 2rem 0.6rem;
`;
export const MemberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.1rem;
  padding: 0.9rem 2rem;
  &:hover { background: ${t.color.canvas}; }
`;
export const MemberInfo = styled.div`
  flex: 1;
  min-width: 0;
  .name {
    font-family: ${t.font.medium};
    font-size: 1.45rem;
    color: ${t.color.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .status {
    font-size: 1.15rem;
    color: ${({ online }) => (online ? t.color.online : t.color.faint)};
    margin-top: 0.1rem;
  }
`;
export const CoachBadge = styled.span`
  font-size: 1.05rem;
  font-family: ${t.font.bold};
  color: ${t.color.primaryDark};
  background: ${t.color.primarySoft};
  border-radius: ${t.radius.pill};
  padding: 0.2rem 0.8rem;
  flex-shrink: 0;
`;

/* ---------------- Empty / skeleton ---------------- */
export const Empty = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  color: ${t.color.faint};
  text-align: center;
  padding: 4rem;
  .emoji { font-size: 5rem; }
  .title { font-family: ${t.font.bold}; font-size: 1.8rem; color: ${t.color.subtext}; }
  .sub { font-size: 1.4rem; max-width: 32rem; }
`;
export const Skeleton = styled.div`
  height: ${({ h }) => h || '1.4rem'};
  width: ${({ w }) => w || '100%'};
  border-radius: ${t.radius.sm};
  background: linear-gradient(90deg, ${t.color.line} 25%, #f4f6f7 37%, ${t.color.line} 63%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s infinite linear;
`;
export const SkeletonRow = styled.div`
  display: flex;
  gap: 1.2rem;
  align-items: center;
  padding: 1.1rem 1.2rem;
`;

export const HintBar = styled.div`
  text-align: center;
  font-size: 1.2rem;
  color: ${t.color.faint};
  padding: 0.8rem;
`;

/* Optimistic "uploading" bubble (own side) shown while media uploads. */
export const UploadingRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1.1rem;
`;
export const UploadingImage = styled.div`
  position: relative;
  width: 18rem;
  height: 18rem;
  border-radius: 1.4rem;
  overflow: hidden;
  background: ${t.color.canvas};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: brightness(0.7);
  }
`;
export const UploadingFile = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.3rem;
  border-radius: ${t.radius.md};
  background: ${t.color.primarySoft};
  color: ${t.color.secondary};
  font-size: 1.4rem;
`;
export const Spinner = styled.div`
  width: ${({ size }) => size || '2.4rem'};
  height: ${({ size }) => size || '2.4rem'};
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  animation: ${keyframes`to { transform: rotate(360deg); }`} 0.7s linear infinite;
  ${({ centered }) =>
    centered &&
    css`
      position: absolute;
      top: 50%;
      left: 50%;
      margin: -1.2rem 0 0 -1.2rem;
    `}
`;
export const SpinnerDark = styled(Spinner)`
  border: 3px solid ${t.color.line};
  border-top-color: ${t.color.primaryDark};
`;

/* Message skeletons shown while a conversation's history loads. */
export const MsgSkeletonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  padding: 1rem 0;
`;
export const MsgSkeletonRow = styled.div`
  display: flex;
  justify-content: ${({ owned }) => (owned ? 'flex-end' : 'flex-start')};
`;
export const MsgSkeletonBubble = styled.div`
  height: ${({ h }) => h || '3.4rem'};
  width: ${({ w }) => w || '22rem'};
  border-radius: 1.8rem;
  background: linear-gradient(90deg, ${t.color.line} 25%, #f4f6f7 37%, ${t.color.line} 63%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s infinite linear;
`;
